import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IonSpinner } from '@ionic/react';
import api from '../../services/api';
import PillButton from '../shared/PillButton';

export const COLLABORATION_VENDORS = [
  { id: 'claude', label: 'Claude' },
  { id: 'codex', label: 'Codex' },
  { id: 'gemini', label: 'Gemini' },
];

/**
 * The one collaboration setup surface: select connected vendor credentials.
 * There is intentionally no model, protocol, role, timeout or async control.
 */
export default function CollaborationCredentials({
  workflowUuid,
  projectId,
  setToast,
  onSaved,
  sectioned = false,
}) {
  const [loading, setLoading] = useState(!!workflowUuid);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  const [available, setAvailable] = useState([]);
  const [counts, setCounts] = useState({ claude: 0, codex: 0, gemini: 0 });
  const totalSeats = Object.values(counts).reduce((sum, count) => sum + (Number(count) || 0), 0);

  useEffect(() => {
    if (!workflowUuid) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    api.get(`/workflows/${encodeURIComponent(workflowUuid)}/participant-bindings`)
      .then((data) => {
        if (!alive) return;
        const bindings = Array.isArray(data?.bindings) ? data.bindings : [];
        const next = { claude: 0, codex: 0, gemini: 0 };
        bindings.filter((binding) => binding?.enabled !== false).forEach((binding) => {
          if (Object.prototype.hasOwnProperty.call(next, binding.id)) {
            next[binding.id] = Math.max(1, Number(binding.instances) || 1);
          }
        });
        setCounts(next);
        setAvailable(Array.isArray(data?.availableVendors) ? data.availableVendors : []);
        setVersion(Number.isInteger(data?.version) ? data.version : 0);
      })
      .catch((err) => setToast?.({
        show: true,
        message: err?.body?.error || err?.message || 'Could not load AI collaborators',
        color: 'danger',
      }))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [workflowUuid, setToast]);

  const setCount = (vendor, value) => {
    const count = Math.max(0, Math.min(8, Number(value) || 0));
    setCounts((current) => ({ ...current, [vendor]: count }));
  };

  const save = async () => {
    if (saving || !workflowUuid) return;
    if (totalSeats > 8) {
      setToast?.({ show: true, message: 'AI Council supports at most 8 worker seats', color: 'danger' });
      return;
    }
    setSaving(true);
    try {
      const data = await api.put(`/workflows/${encodeURIComponent(workflowUuid)}/participant-bindings`, {
        vendorCounts: counts,
        expectedVersion: version,
      });
      setVersion(Number.isInteger(data?.version) ? data.version : version + 1);
      setToast?.({ show: true, message: 'AI collaborators saved', color: 'success' });
      if (typeof onSaved === 'function') await onSaved();
    } catch (err) {
      setToast?.({
        show: true,
        message: err?.body?.error || err?.message || 'Could not save AI collaborators',
        color: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={sectioned ? 'pipeline-section pipeline-section--separated' : 'gwe-collaboration-credentials'}>
      <h3 className={sectioned ? 'pipeline-section-title' : 'gwe-subgraph-panel__title'}>AI collaborators</h3>
      <p className={sectioned ? 'pipeline-section-hint' : 'gwe-subgraph-panel__body'}>
        Choose which connected AI credentials this node may invite. The agent already knows when and how to use them.
      </p>
      {loading ? <IonSpinner name="crescent" /> : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '14px 0' }}>
            {COLLABORATION_VENDORS.map(({ id, label }) => {
              const count = counts[id] || 0;
              const connected = available.includes(id) || count > 0;
              return (
                <label key={id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                  border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10,
                  opacity: connected ? 1 : 0.45, cursor: connected ? 'pointer' : 'not-allowed',
                }}>
                  <span>{label}</span>
                  <input
                    type="number"
                    min="0"
                    max="8"
                    value={count}
                    disabled={!connected || saving || !workflowUuid}
                    onChange={(event) => setCount(id, event.target.value)}
                    aria-label={`${label} Council seats`}
                    style={{ width: 52 }}
                  />
                  <small style={{ opacity: 0.65 }}>{connected ? (count === 1 ? '1 worker' : `${count} workers`) : 'not connected'}</small>
                </label>
              );
            })}
          </div>
          {available.length === 0 && Object.values(counts).every((count) => count === 0) && projectId && (
            <p className={sectioned ? 'pipeline-section-hint' : 'gwe-subgraph-panel__hint'}>
              Connect Claude, Codex or Gemini in <Link to={`/projects/${projectId}/settings?section=ai`}>Project AI Settings</Link>.
            </p>
          )}
          <p className={sectioned ? 'pipeline-section-hint' : 'gwe-subgraph-panel__hint'}>
            {totalSeats}/8 worker seats · the Chair runs after every seat is terminal
          </p>
          <PillButton loading={saving} disabled={saving || !workflowUuid || totalSeats > 8} onClick={save}>Save</PillButton>
        </>
      )}
    </div>
  );
}
